import { useState, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { exportToExcel } from '@/utils/csvExport';
import { getAvailableTransitions, STATE_LABELS, STATE_COLORS, WORKFLOW_STATES, getPrimaryStatus, PRIMARY_STATE_LABELS, PRIMARY_STATE_COLORS, getSubStatusLabel, SUB_STATE_COLORS } from '@/lib/workflow';
import { ROLES } from '@/lib/roles';
import { canFastwebTakeover, hasFastwebTakeover, canApproveDocuments, canRejectDocuments } from '@/lib/practicePermissions';
import { CATEGORIE_RIFIUTO, validateCategorieRifiuto } from '@/lib/rejectNote';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Download, Loader2, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown,
  Pencil, Trash2, AlertCircle, CheckCircle2, Upload, Clock,
} from 'lucide-react';
import PracticeForm from '@/components/PracticeForm';
import { formatDate } from '@/utils/formatters';

const PAGE_SIZE = 25;

const COLUMNS = [
  { key: 'id', label: 'ID Lav.' },
  { key: 'codiceSito', label: 'Cod. Sito' },
  { key: 'nomeSito', label: 'Nome Sito' },
  { key: 'tipologiaLavorazione', label: 'Tipo Intervento' },
  { key: 'ditta', label: 'Ditta' },
  { key: 'studioProgettazione', label: 'Studio Prog.' },
  { key: 'comune', label: 'Comune' },
  { key: 'importo', label: 'Importo', right: true },
  { key: 'workflowStatus', label: 'Stato Lav.' },
  { key: 'docCaricati', label: 'Doc Caricati' },
];

export default function PracticeTable({ practices, onDelete, permissions = {}, entryLastSeen }) {
  const { role, user, userProfile } = useAuth();
  const {
    transitionPractice, setDocCaricati, practicesLimitReached,
    dittaApprovePreventivo, dittaRejectPreventivo, fornitoreAggiornaPreventivo,
    setFastwebTakeover,
  } = useData();
  const canSetDoc = permissions.canSetDocCaricati === true;
  const canEdit = permissions.canEditPractice !== false;
  const canDelete = permissions.canDeletePractice !== false;
  const canExport = permissions.canExportCSV !== false;
  const showActions = canEdit || canDelete;
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [editPractice, setEditPractice] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // rejectTarget: { practice, mode: 'hq' | 'ditta' }
  // - mode 'hq'    → rifiuto documenti HQ      (transitionPractice → 'rifiutato')
  // - mode 'ditta' → rifiuto preventivo Ditta  (dittaRejectPreventivo → 'rifiutato_ditta')
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  // categorie del rifiuto documenti (multi-selezione, solo mode 'hq')
  const [rejectCategorie, setRejectCategorie] = useState([]);
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState(null);
  // quotationTarget: pratica in stato rifiutato_ditta da cui il fornitore aggiorna l'importo
  const [quotationTarget, setQuotationTarget] = useState(null);
  const [quotationValue, setQuotationValue] = useState('');
  const [quotationLoading, setQuotationLoading] = useState(false);
  const [quotationError, setQuotationError] = useState(null);

  const isHighlighted = useCallback(
    (p) => entryLastSeen && p.updatedAt?.toDate?.() > entryLastSeen && p.lastUpdatedBy !== user?.uid,
    [entryLastSeen, user?.uid]
  );

  const sorted = useMemo(() => {
    return [...practices].sort((a, b) => {
      // Prima: pratiche evidenziate in cima
      if (entryLastSeen) {
        const aH = isHighlighted(a) ? 1 : 0;
        const bH = isHighlighted(b) ? 1 : 0;
        if (aH !== bH) return bH - aH;
      }
      // Poi: ordinamento per colonna (preservato dentro ogni gruppo)
      if (sortKey) {
        let va = a[sortKey];
        let vb = b[sortKey];
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [practices, sortKey, sortDir, entryLastSeen, isHighlighted]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const SortIcon = ({ colKey }) => {
    if (sortKey !== colKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-fw-yellow" />
      : <ArrowDown className="w-3 h-3 ml-1 text-fw-yellow" />;
  };

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  // Clamp della pagina corrente: se i risultati si riducono (filtri applicati
  // mentre si e' su una pagina successiva, o pratiche rimosse in real-time) la
  // pagina salvata puo' eccedere quelle disponibili → senza clamp la slice
  // sarebbe vuota (contatore giusto, tabella vuota).
  const currentPage = Math.min(page, totalPages - 1);
  const paged = sorted.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const handleReject = async () => {
    if (!rejectTarget) return;
    if (!rejectNote.trim()) { setRejectError('Le note sono obbligatorie'); return; }
    if (rejectTarget.mode !== 'ditta') {
      const catError = validateCategorieRifiuto(WORKFLOW_STATES.RIFIUTATO, rejectCategorie);
      if (catError) { setRejectError(catError); return; }
    }
    setRejecting(true);
    setRejectError(null);
    try {
      const trimmed = rejectNote.trim();
      if (rejectTarget.mode === 'ditta') {
        await dittaRejectPreventivo(rejectTarget.practice.id, trimmed);
      } else {
        await transitionPractice(rejectTarget.practice.id, WORKFLOW_STATES.RIFIUTATO, role, trimmed, rejectCategorie);
      }
      setRejectTarget(null);
      setRejectNote('');
      setRejectCategorie([]);
    } catch (err) {
      setRejectError(err.message);
    } finally {
      setRejecting(false);
    }
  };

  const handleUpdateQuotation = async () => {
    if (!quotationTarget) return;
    const num = parseFloat(quotationValue);
    if (!Number.isFinite(num) || num < 0) {
      setQuotationError('Importo non valido (deve essere un numero ≥ 0)');
      return;
    }
    setQuotationLoading(true);
    setQuotationError(null);
    try {
      await fornitoreAggiornaPreventivo(quotationTarget.id, num);
      setQuotationTarget(null);
      setQuotationValue('');
    } catch (err) {
      setQuotationError(err.message);
    } finally {
      setQuotationLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await onDelete(deleteTarget);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-fw-border shadow-sm overflow-hidden" data-testid="practices-table-container">
        <div className="flex items-center justify-between px-6 py-4 border-b border-fw-border/50 border-l-4 border-l-fw-yellow">
          <h2 className="text-sm font-semibold text-fw-dark">
            Attività <span className="text-fw-subtle font-normal">({practices.length} risultati)</span>
          </h2>
          {canExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportToExcel(practices)}
              className="border-fw-yellow/30 text-fw-dark hover:bg-yellow-50"
              data-testid="export-excel-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Esporta Excel
            </Button>
          )}
        </div>

        {practicesLimitReached && (
          <div className="px-6 py-2 bg-yellow-50 border-b border-fw-yellow/30 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-fw-yellow-hover shrink-0" />
            <p className="text-xs text-fw-muted">
              Visualizzate le ultime 5.000 pratiche. Utilizza i filtri per restringere i risultati.
            </p>
          </div>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-fw-dark hover:bg-fw-dark">
                {COLUMNS.map((col) => (
                  <TableHead
                    key={col.key}
                    className={`text-xs font-semibold uppercase tracking-wider text-fw-subtle whitespace-nowrap cursor-pointer select-none hover:text-fw-yellow transition-colors ${col.right ? 'text-right' : ''}`}
                    onClick={() => handleSort(col.key)}
                  >
                    <span className="inline-flex items-center">
                      {col.label}
                      <SortIcon colKey={col.key} />
                    </span>
                  </TableHead>
                ))}
                {showActions && <TableHead className="text-xs font-semibold uppercase tracking-wider text-fw-subtle whitespace-nowrap text-center w-20">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.length === 0 && (
                <TableRow>
                  <TableCell colSpan={showActions ? 11 : 10} className="text-center py-12 text-fw-subtle">
                    {practices.length === 0
                      ? (role === ROLES.FORNITORE
                          ? 'Nessuna attività presente. Clicca su "Aggiungi Attività" per crearne una.'
                          : 'Nessuna attività presente.')
                      : 'Nessun risultato con i filtri selezionati.'}
                  </TableCell>
                </TableRow>
              )}
              {paged.map((p) => (
                <TableRow key={p.id || p._docId} className={`hover:bg-yellow-50/50 transition-colors duration-150 border-b border-fw-border/30 ${isHighlighted(p) ? 'bg-yellow-50 border-l-4 border-l-fw-yellow' : ''}`}>
                  <TableCell>
                    <div className="font-mono text-sm font-bold text-fw-dark">{p.id}</div>
                    <div className="text-[10px] text-fw-subtle mt-0.5 leading-relaxed">
                      <div>Inizio: {formatDate(p.dataInizio)}</div>
                      {p.dataApprovazioneDocumenti && <div>App. Doc: {formatDate(p.dataApprovazioneDocumenti)}</div>}
                      {p.dataFine && <div>Fine: {formatDate(p.dataFine)}</div>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm font-bold text-fw-dark whitespace-nowrap uppercase">{p.codiceSito || '–'}</TableCell>
                  <TableCell className="text-sm font-bold text-fw-dark uppercase">{p.nomeSito || '–'}</TableCell>
                  <TableCell className="text-sm font-bold text-fw-muted uppercase">{p.tipologiaLavorazione || '–'}</TableCell>
                  <TableCell className="text-sm font-bold text-fw-muted uppercase">{p.ditta || '–'}</TableCell>
                  <TableCell className="text-sm font-bold text-fw-dark uppercase">
                    <div className="flex flex-col gap-0.5">
                      <span>{p.studioProgettazione || '–'}</span>
                      {p.fornitoreIsInternalHq === true && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold normal-case text-fw-purple bg-fw-purple-subtle px-1.5 py-0.5 rounded w-fit"
                          title="Professionista interno HQ: controllore documenti = Fastweb"
                        >
                          Interno HQ
                        </span>
                      )}
                      {hasFastwebTakeover(p) && (role === ROLES.FASTWEB || role === ROLES.HQ) && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold normal-case text-fw-dark bg-fw-yellow px-1.5 py-0.5 rounded w-fit"
                          title="Fastweb ha preso in carico questa pratica come controllore documenti (HQ esclusa)"
                        >
                          In carico Fastweb
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm font-bold text-fw-muted uppercase">{p.comune ? `${p.comune}${p.provincia ? ` (${p.provincia.toUpperCase()})` : ''}` : '–'}</TableCell>
                  <TableCell className="text-sm font-bold text-fw-dark text-right whitespace-nowrap">
                    {p.importo != null
                      ? <span className="font-mono">{p.importo.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                      : '–'}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const ws = p.workflowStatus || WORKFLOW_STATES.NON_AVVIATO;
                      const primary = getPrimaryStatus(ws);
                      const pc = PRIMARY_STATE_COLORS[primary];
                      const subLabel = getSubStatusLabel(ws, p);
                      const subColors = SUB_STATE_COLORS[ws];
                      // Gate ETO: le transizioni da controllore documenti (Approva doc /
                      // Rifiuta) per il ruolo Fastweb richiedono il profilo ETO. Gli utenti
                      // Fastweb territoriali (consultazione) non vedono i bottoni.
                      const transitions = getAvailableTransitions(ws, role, p).filter((t) => {
                        if (ws === WORKFLOW_STATES.IN_CORSO && t.to === WORKFLOW_STATES.APPROVAZIONE_HQ) {
                          return canApproveDocuments(role, p, userProfile);
                        }
                        if (t.to === WORKFLOW_STATES.RIFIUTATO) {
                          return canRejectDocuments(role, p, userProfile);
                        }
                        return true;
                      });
                      const badgeNote = ws === WORKFLOW_STATES.RIFIUTATO ? p.noteRifiuto
                        : ws === WORKFLOW_STATES.RIFIUTATO_DITTA ? p.noteRifiutoDitta
                        : null;
                      return (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge
                              className={`border-0 ${pc.bg} ${pc.text} text-xs whitespace-nowrap`}
                            >
                              {PRIMARY_STATE_LABELS[primary]}
                            </Badge>
                            {subLabel && subColors && (
                              <Badge
                                className={`border-0 ${subColors.bg} ${subColors.text} text-xs whitespace-nowrap`}
                                title={badgeNote ? `Note: ${badgeNote}` : undefined}
                              >
                                {subLabel}
                              </Badge>
                            )}
                            {canFastwebTakeover(role, p, userProfile) && (
                              <button
                                onClick={() => setFastwebTakeover(p.id)}
                                className="text-xs px-1.5 py-0.5 rounded border border-fw-yellow bg-yellow-50 text-fw-dark hover:bg-fw-yellow hover:text-fw-dark transition-colors whitespace-nowrap min-w-[70px] text-center font-semibold"
                                title="Prendi in carico come controllore documenti (HQ verra' esclusa). Azione IRREVERSIBILE."
                              >
                                Prendo in carico
                              </button>
                            )}
                            {transitions.map((t) => (
                              <button
                                key={t.to}
                                onClick={() => {
                                  // Rifiuto documenti HQ (in_corso → rifiutato)
                                  if (t.to === WORKFLOW_STATES.RIFIUTATO) {
                                    setRejectTarget({ practice: p, mode: 'hq' });
                                    setRejectNote('');
                                    setRejectError(null);
                                  // Rifiuto preventivo Ditta (approvazione_hq → rifiutato_ditta)
                                  } else if (t.to === WORKFLOW_STATES.RIFIUTATO_DITTA) {
                                    setRejectTarget({ practice: p, mode: 'ditta' });
                                    setRejectNote('');
                                    setRejectError(null);
                                  // Approvazione preventivo Ditta (approvazione_hq → chiuso)
                                  } else if (t.to === WORKFLOW_STATES.CHIUSO && role === ROLES.DITTA) {
                                    dittaApprovePreventivo(p.id);
                                  // Aggiorna preventivo Fornitore (rifiutato_ditta → approvazione_hq)
                                  } else if (t.to === WORKFLOW_STATES.APPROVAZIONE_HQ
                                          && role === ROLES.FORNITORE
                                          && ws === WORKFLOW_STATES.RIFIUTATO_DITTA) {
                                    setQuotationTarget(p);
                                    setQuotationValue(p.importo != null ? String(p.importo) : '');
                                    setQuotationError(null);
                                  // Approvazione documenti HQ + riapri Fornitore + altri
                                  } else {
                                    transitionPractice(p.id, t.to, role);
                                  }
                                }}
                                className="text-xs px-1.5 py-0.5 rounded border border-fw-border-strong hover:bg-fw-light text-fw-muted hover:text-fw-dark transition-colors whitespace-nowrap min-w-[70px] text-center"
                                title={t.label}
                              >
                                {t.label}
                              </button>
                            ))}
                          </div>
                          {badgeNote && (
                            <p className="text-xs text-red-600 italic line-clamp-2 max-w-[220px]" title={badgeNote}>
                              "{badgeNote}"
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-center">
                    {p.docCaricati ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                        <CheckCircle2 className="w-3 h-3" /> Caricati
                      </span>
                    ) : canSetDoc ? (
                      <button
                        onClick={() => setDocCaricati(p.id, true)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fw-light text-fw-muted text-xs font-medium hover:bg-yellow-50 hover:text-fw-dark transition-colors"
                        title="Segna documenti come caricati"
                      >
                        <Upload className="w-3 h-3" /> Da caricare
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fw-light text-fw-subtle text-xs font-medium">
                        <Clock className="w-3 h-3" /> Da caricare
                      </span>
                    )}
                  </TableCell>
                  {showActions && (
                    <TableCell className="text-center">
                      {(() => {
                        const ws = p.workflowStatus || 'non_avviato';
                        const canModify = ws === 'non_avviato';
                        return (
                          <div className="flex items-center justify-center gap-1">
                            {canEdit && (
                              <button
                                onClick={() => canModify && setEditPractice(p)}
                                disabled={!canModify}
                                className={`p-1 rounded transition-colors ${canModify ? 'hover:bg-yellow-50 text-fw-subtle hover:text-fw-yellow' : 'text-fw-border cursor-not-allowed'}`}
                                title={canModify ? 'Modifica' : 'Non modificabile in questo stato'}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => canModify && setDeleteTarget(String(p.id))}
                                disabled={!canModify}
                                className={`p-1 rounded transition-colors ${canModify ? 'hover:bg-red-50 text-fw-subtle hover:text-fw-red' : 'text-fw-border cursor-not-allowed'}`}
                                title={canModify ? 'Elimina' : 'Non eliminabile in questo stato'}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-fw-border/50 bg-fw-light" data-testid="pagination">
            <p className="text-sm text-fw-muted">
              Pagina {currentPage + 1} di {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)} data-testid="prev-page-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setPage(currentPage + 1)} data-testid="next-page-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {editPractice && (
        <PracticeForm editPractice={editPractice} onClose={() => setEditPractice(null)} />
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>Stai per eliminare l'attività <strong>{deleteTarget}</strong>. Questa azione è irreversibile.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annulla</Button>
            <Button className="bg-fw-red hover:bg-red-700 text-white" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {deleting ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog rifiuto con note obbligatorie — gestisce sia rifiuto HQ (documenti) sia rifiuto Ditta (preventivo) */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectNote(''); setRejectCategorie([]); setRejectError(null); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {rejectTarget?.mode === 'ditta' ? 'Rifiuta Preventivo' : 'Rifiuta Documenti'}
            </DialogTitle>
            <DialogDescription>
              {rejectTarget?.mode === 'ditta' ? (
                <>Stai rifiutando il preventivo della pratica <strong>{rejectTarget?.practice?.id}</strong>. Le note sono obbligatorie e verranno inviate al professionista.</>
              ) : (
                <>Stai rifiutando i documenti della pratica <strong>{rejectTarget?.practice?.id}</strong>. Le note sono obbligatorie e verranno inviate al professionista.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {rejectTarget?.mode !== 'ditta' && (
              <div>
                <label className="text-sm font-medium text-fw-dark">Categoria del rifiuto * <span className="font-normal text-xs text-fw-subtle">(una o più)</span></label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {CATEGORIE_RIFIUTO.map((cat) => {
                    const selected = rejectCategorie.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setRejectCategorie((prev) =>
                          selected ? prev.filter((c) => c !== cat) : [...prev, cat]
                        )}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                          selected
                            ? 'bg-fw-yellow border-fw-yellow text-fw-dark'
                            : 'bg-white border-fw-border text-fw-muted hover:border-fw-yellow hover:text-fw-dark'
                        }`}
                      >
                        {selected ? '✓ ' : ''}{cat}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-fw-dark">Note di rifiuto *</label>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder={rejectTarget?.mode === 'ditta'
                  ? 'Spiega perché il preventivo non è accettabile (es. importo fuori budget)...'
                  : 'Spiega il motivo del rifiuto dei documenti...'}
                rows={4}
                className="mt-1 w-full border border-fw-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-fw-yellow focus:border-fw-yellow outline-none resize-none"
              />
            </div>
            {rejectError && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                <AlertCircle className="w-4 h-4 shrink-0" /> {rejectError}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectNote(''); setRejectCategorie([]); setRejectError(null); }} disabled={rejecting}>Annulla</Button>
            <Button className="bg-fw-red hover:bg-red-700 text-white" onClick={handleReject} disabled={rejecting || !rejectNote.trim() || (rejectTarget?.mode !== 'ditta' && rejectCategorie.length === 0)}>
              {rejecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {rejecting ? 'Rifiuto...' : 'Conferma Rifiuto'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog aggiornamento preventivo (Fornitore: rifiutato_ditta → approvazione_hq) */}
      <Dialog open={!!quotationTarget} onOpenChange={(o) => { if (!o) { setQuotationTarget(null); setQuotationValue(''); setQuotationError(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Aggiorna Preventivo</DialogTitle>
            <DialogDescription>
              La Ditta ha rifiutato il preventivo per la pratica <strong>{quotationTarget?.id}</strong>.
              {quotationTarget?.noteRifiutoDitta && (
                <span className="block mt-2 text-xs italic text-red-600">"{quotationTarget.noteRifiutoDitta}"</span>
              )}
              <span className="block mt-2 text-xs">Inserisci il nuovo importo. La pratica torna direttamente alla Ditta — HQ non riapprova.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-fw-dark">Nuovo importo (€) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={quotationValue}
                onChange={(e) => setQuotationValue(e.target.value)}
                placeholder="0.00"
                className="mt-1 w-full border border-fw-border rounded-lg px-3 py-2 text-sm font-mono text-right focus:ring-2 focus:ring-fw-yellow focus:border-fw-yellow outline-none"
              />
              {quotationTarget?.importo != null && (
                <p className="text-xs text-fw-subtle mt-1">
                  Importo precedente: <span className="font-mono">{quotationTarget.importo.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                </p>
              )}
            </div>
            {quotationError && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                <AlertCircle className="w-4 h-4 shrink-0" /> {quotationError}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setQuotationTarget(null); setQuotationValue(''); setQuotationError(null); }} disabled={quotationLoading}>Annulla</Button>
            <Button className="bg-fw-yellow hover:bg-fw-yellow-hover text-fw-dark" onClick={handleUpdateQuotation} disabled={quotationLoading || !quotationValue.trim()}>
              {quotationLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {quotationLoading ? 'Invio...' : 'Reinvia alla Ditta'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
